
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from 'react-i18next';

interface AnalysisSummaryCardProps {
  riskLevel: 'low' | 'medium' | 'high';
  analysis: string;
}

export const AnalysisSummaryCard = ({ riskLevel, analysis }: AnalysisSummaryCardProps) => {
  const { t } = useTranslation();
  const getRiskColor = (level: 'low' | 'medium' | 'high') => {
    switch(level) {
      case 'low': return 'bg-green-500/20 text-green-700 border-green-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-700 border-yellow-500/30';
      case 'high': return 'bg-red-500/20 text-red-700 border-red-500/30';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-background via-background to-purple-500/5 border border-border/50 backdrop-blur-sm hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300 hover:-translate-y-1 group card-shine soft-glow">
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-transparent to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_40%_60%,rgba(139,92,246,0.08),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="absolute inset-0 shimmer-slide opacity-0 group-hover:opacity-30 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      <CardHeader className="relative z-10 pb-4">
        <CardTitle className="text-foreground flex items-center gap-2 group-hover:text-purple-600 transition-colors duration-300">
          <div className="p-2 rounded-lg bg-yellow-500/10 text-yellow-500 group-hover:bg-yellow-500/20 transition-all duration-300 floating-action icon-spin-hover">
            <AlertTriangle className="h-5 w-5" />
          </div>
          {t('market_analysis.analysis_summary')}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="relative z-10 space-y-6">
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-accent/30 via-accent/20 to-transparent rounded-xl hover:from-accent/50 hover:via-accent/30 hover:to-accent/10 transition-all duration-300 backdrop-blur-sm border border-border/20 hover:border-border/40 card-hover-effect">
          <span className="text-foreground font-medium">{t('market_analysis.risk_level')}</span>
          <Badge className={`${getRiskColor(riskLevel)} transition-all duration-300 hover:scale-105 pulse-soft`}>
            {riskLevel.toUpperCase()}
          </Badge>
        </div>
        
        <div className="p-5 bg-gradient-to-br from-accent/30 via-accent/20 to-transparent rounded-xl hover:from-accent/50 hover:via-accent/30 hover:to-accent/10 transition-all duration-300 backdrop-blur-sm border border-border/20 hover:border-border/40 card-hover-effect backdrop-blur-subtle">
          <p className="text-muted-foreground leading-relaxed text-sm">
            {analysis}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
